import { joiResolver } from '@hookform/resolvers/joi';
import { BaseSyntheticEvent } from 'react';
import {
  Control,
  DeepMap,
  FieldError,
  FieldValues,
  FormState,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  FORGOT_PASSWORD_CLICK_TIME,
  ResponseMessage,
} from '../../../constants';
import {
  ForgotPasswordForm,
  forgotPasswordSchema,
  LoginStep,
} from '../../../forms';
import useInject from '../../../hooks/useInject';

type UseViewModelReturn = {
  control: Control<ForgotPasswordForm>;
  handleSubmit: <TSubmitFieldValues extends FieldValues = ForgotPasswordForm>(
    onValid: SubmitHandler<TSubmitFieldValues>,
    onInvalid?: SubmitErrorHandler<ForgotPasswordForm> | undefined
  ) => (e?: BaseSyntheticEvent) => Promise<void>;
  errors: DeepMap<ForgotPasswordForm, FieldError>;
  formState: FormState<ForgotPasswordForm>;
  isDirty: boolean;
  isSubmitted: boolean;
  isValid: boolean;
  handleOnSubmit: (data: ForgotPasswordForm) => Promise<void>;
  setLoginStep: (step: LoginStep) => void;
};

const useViewModel = (): UseViewModelReturn => {
  const { control, handleSubmit, errors, formState } =
    useForm<ForgotPasswordForm>({
      mode: 'onChange',
      resolver: joiResolver(forgotPasswordSchema),
    });
  const { isDirty, isSubmitted, isValid } = formState;
  const { forgotPassword, setLoginStep, setForgotPwdEmail } = useInject(
    (root) => root
  );

  const handleOnSubmit = async (data: ForgotPasswordForm): Promise<void> => {
    const response: string = await forgotPassword(data);
    if (response === ResponseMessage.OK) {
      setForgotPwdEmail(data.email);
      setLoginStep(LoginStep.CheckEmail);
      window.sessionStorage.setItem(
        FORGOT_PASSWORD_CLICK_TIME,
        new Date().toLocaleString()
      );
    }
  };

  return {
    control,
    handleSubmit,
    errors,
    formState,
    isDirty,
    isSubmitted,
    isValid,
    handleOnSubmit,
    setLoginStep,
  };
};

export default useViewModel;
